import { useCallback, useEffect, useMemo, useState } from 'react'
import { AppLayout } from '@/components/layout/AppLayout'
import { Button } from '@/components/ui/Button'
import { Dropdown } from '@/components/ui/Dropdown'
import { Skeleton } from '@/components/ui/Skeleton'
import { ReviewSession } from '@/components/review/ReviewSession'
import { PracticeCountModal } from '@/components/review/PracticeCountModal'
import { AddWordModal } from '@/components/vocabulary/AddWordModal'
import { AddPhraseModal } from '@/components/vocabulary/AddPhraseModal'
import { EditCustomCardModal } from '@/components/vocabulary/EditCustomCardModal'
import { EditPhraseModal } from '@/components/vocabulary/EditPhraseModal'
import { DeckStrip } from '@/components/vocabulary/DeckStrip'
import { ResetVocabularyModal } from '@/components/vocabulary/ResetVocabularyModal'
import { EditStrengthModal } from '@/components/vocabulary/EditStrengthModal'
import { STATUS_FILLS, VocabularyCard, VocabularyRow } from '@/components/vocabulary/VocabularyRow'
import { useIsPhone } from '@/hooks/useIsPhone'
import { fetchVocabulary } from '@/api/vocabulary'
import { fetchDecks } from '@/api/decks'
import { useLanguages } from '@/hooks/useLanguages'
import { useStaggerIn } from '@/hooks/useStaggerIn'
import { cn } from '@/utils/cn'
import { STATUS_LABELS } from '@/utils/vocabularyStatus'
import type { CustomVocabularyEntry, VocabularyEntry, WordVocabularyEntry } from '@/types/vocabulary'
import type { Deck } from '@/types/deck'
import type { WordStatus } from '@/types/word'
import { focusRing, segmentedRest, segmentedSelected, softFill } from '@/components/ui/buttonStyles'
import { PageContainer } from '@/components/layout/PageContainer'
import { NoLanguagesEmptyState } from '@/components/languages/NoLanguagesEmptyState'

/* ─── Constants ─────────────────────────────────────────── */

type SortKey = 'lemma' | 'status' | 'next_review_at'
type SortDir = 'asc' | 'desc'

const STATUS_ORDER: Record<WordStatus, number> = {
  new: 0, learning: 1, review: 2, mastered: 3,
}

const ALL_STATUSES: (WordStatus | 'all')[] = ['all', 'new', 'learning', 'review', 'mastered']

type TypeFilter = 'all' | 'word' | 'phrase'

const TYPE_FILTERS: { value: TypeFilter; label: string }[] = [
  // Not plain "All": the status row below has its own "All" chip right beside it.
  { value: 'all', label: 'All types' },
  { value: 'word', label: 'Words' },
  { value: 'phrase', label: 'Phrases' },
]

function entryKind(entry: VocabularyEntry): 'word' | 'phrase' {
  return entry.type === 'custom' && entry.card_type === 'phrase' ? 'phrase' : 'word'
}

/* ─── Main component ────────────────────────────────────── */

export default function VocabularyIndex() {
  const { languages, loading: langLoading, selectedLanguageId } = useLanguages()
  const enrolled = languages.filter((l) => l.enrolled)
  const selected = enrolled.find((l) => l.id === selectedLanguageId) ?? enrolled[0] ?? null

  const selectedId = selected?.id ?? null

  const [entries, setEntries] = useState<VocabularyEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<WordStatus | 'all'>('all')
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('lemma')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [reviewing, setReviewing] = useState(false)
  const [choosingPractice, setChoosingPractice] = useState(false)
  const [practiceCount, setPracticeCount] = useState<number | null>(null)
  const [adding, setAdding] = useState(false)
  const [addingPhrase, setAddingPhrase] = useState(false)
  const [editing, setEditing] = useState<CustomVocabularyEntry | null>(null)
  const [editingStrength, setEditingStrength] = useState<WordVocabularyEntry | null>(null)
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  const [decks, setDecks] = useState<Deck[]>([])
  const [studyDeckId, setStudyDeckId] = useState<number | null>(null)
  const [resetting, setResetting] = useState(false)
  const [addMenuOpen, setAddMenuOpen] = useState(false)
  const [moreMenuOpen, setMoreMenuOpen] = useState(false)

  const loadVocabulary = useCallback(() => {
    if (selectedId === null) return
    setLoading(true)
    fetchVocabulary(selectedId)
      .then((res) => setEntries(res.data.data))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false))
  }, [selectedId])

  useEffect(() => {
    loadVocabulary()
  }, [loadVocabulary])

  const loadDecks = useCallback(() => {
    if (selectedId === null) return
    fetchDecks(selectedId)
      .then((res) => setDecks(res.data.data))
      .catch(() => setDecks([]))
  }, [selectedId])

  useEffect(() => {
    loadDecks()
  }, [loadDecks])

  const refresh = useCallback(() => {
    loadVocabulary()
    loadDecks()
  }, [loadVocabulary, loadDecks])

  const handleReviewClose = useCallback(() => {
    setReviewing(false)
    setStudyDeckId(null)
    setPracticeCount(null)
    refresh()
  }, [refresh])

  const handleSort = useCallback((key: SortKey) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
        return key
      }
      setSortDir(key === 'lemma' ? 'asc' : 'desc')
      return key
    })
  }, [])

  const filtered = useMemo(() => {
    let result = entries
    if (typeFilter !== 'all') {
      result = result.filter((e) => entryKind(e) === typeFilter)
    }
    if (filter !== 'all') {
      result = result.filter((e) => e.status === filter)
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      result = result.filter((e) =>
        e.lemma.toLowerCase().includes(q) ||
        e.translations.some((t) => t.toLowerCase().includes(q))
      )
    }
    return result
  }, [entries, typeFilter, filter, search])

  const sorted = useMemo(() => {
    const mult = sortDir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      switch (sortKey) {
        case 'lemma':
          return mult * a.lemma.localeCompare(b.lemma)
        case 'status':
          return mult * (STATUS_ORDER[a.status] - STATUS_ORDER[b.status])
        case 'next_review_at':
          return mult * (new Date(a.next_review_at).getTime() - new Date(b.next_review_at).getTime())
        default:
          return 0
      }
    })
  }, [filtered, sortKey, sortDir])

  const stats = useMemo(() => {
    const counts: Record<WordStatus, number> = { new: 0, learning: 0, review: 0, mastered: 0 }
    for (const e of entries) counts[e.status]++
    return counts
  }, [entries])

  const dueCount = useMemo(() => {
    const now = Date.now()
    return entries.filter((e) => new Date(e.next_review_at).getTime() <= now).length
  }, [entries])

  // With no language selected nothing is fetched, so `loading` never clears; it only
  // describes a fetch that is actually happening (Mimir-Languages-hgl).
  const isLoading = langLoading || (selectedId !== null && loading)

  return (
    <AppLayout>
      <PageContainer className="py-12 sm:py-16 lg:py-20">
        {isLoading && <VocabularySkeleton />}

        {!isLoading && !selected && (
          <div className="animate-enter text-center">
            <h1 className="font-serif text-3xl sm:text-4xl font-medium text-heading leading-tight">
              Vocabulary
            </h1>
            <NoLanguagesEmptyState />
          </div>
        )}

        {!isLoading && selected && (
          <>
            <div
              data-testid="vocabulary-header"
              // animate-enter's transform makes this a stacking context, trapping the
              // Add / More menus' z-50 inside it; lift the whole header above the
              // (also animated) sections that follow so the menus aren't covered.
              className="animate-enter relative z-20 flex flex-wrap items-center justify-between gap-4"
            >
              <h1 className="font-serif text-3xl sm:text-4xl font-medium text-heading leading-tight">
                Vocabulary
              </h1>
              <div className="flex flex-wrap items-center gap-2">
                {dueCount > 0 && (
                  <Button onClick={() => setReviewing(true)} className="px-5 py-2.5 min-h-11">
                    Review {dueCount} {dueCount === 1 ? 'card' : 'cards'}
                  </Button>
                )}
                {entries.length > 0 && (
                  <Button
                    variant="secondary"
                    onClick={() => setChoosingPractice(true)}
                    className="px-5 py-2.5 min-h-11"
                  >
                    Practice
                  </Button>
                )}
                <Dropdown
                  open={addMenuOpen}
                  onOpenChange={setAddMenuOpen}
                  align="right"
                  trigger={
                    <button
                      type="button"
                      onClick={() => setAddMenuOpen((o) => !o)}
                      aria-haspopup="menu"
                      aria-expanded={addMenuOpen}
                      className={cn(menuTrigger, 'px-5')}
                    >
                      Add
                    </button>
                  }
                >
                  <div role="menu" aria-label="Add" className="p-1">
                    <MenuItem onSelect={() => { setAddMenuOpen(false); setAdding(true) }}>Word</MenuItem>
                    <MenuItem onSelect={() => { setAddMenuOpen(false); setAddingPhrase(true) }}>Phrase</MenuItem>
                  </div>
                </Dropdown>
                {(entries.length > 0 || decks.length > 0) && (
                  <Dropdown
                    open={moreMenuOpen}
                    onOpenChange={setMoreMenuOpen}
                    align="right"
                    trigger={
                      <button
                        type="button"
                        onClick={() => setMoreMenuOpen((o) => !o)}
                        aria-label="More"
                        aria-haspopup="menu"
                        aria-expanded={moreMenuOpen}
                        className={cn(menuTrigger, 'w-11 justify-center text-lg leading-none')}
                      >
                        <span aria-hidden="true">···</span>
                      </button>
                    }
                  >
                    <div role="menu" aria-label="More" className="p-1">
                      <MenuItem
                        onSelect={() => { setMoreMenuOpen(false); setResetting(true) }}
                        className="text-muted hover:text-error"
                      >
                        Reset vocabulary
                      </MenuItem>
                    </div>
                  </Dropdown>
                )}
              </div>
            </div>

            {/* Stats bar */}
            {entries.length > 0 && (
              <div className="animate-enter animate-enter-d2 mt-8 grid grid-cols-4 gap-2 sm:gap-3">
                <StatCard status="new" label="New" count={stats.new} />
                <StatCard status="learning" label="Learning" count={stats.learning} />
                <StatCard status="review" label="Review" count={stats.review} />
                <StatCard status="mastered" label="Mastered" count={stats.mastered} />
              </div>
            )}

            {/* Phrase decks */}
            <div className="animate-enter animate-enter-d3 mt-10">
              <DeckStrip
                decks={decks}
                languageId={selected.id}
                onStudy={(deckId) => setStudyDeckId(deckId)}
                onChanged={refresh}
              />
            </div>

            {/* Filter + search */}
            {entries.length > 0 && (
              <div className="animate-enter animate-enter-d3 mt-8 flex flex-col lg:flex-row lg:items-center gap-3">
                <div className="flex items-center gap-1.5 -mx-4 px-4 overflow-x-auto sm:mx-0 sm:px-0 sm:flex-wrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {TYPE_FILTERS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setTypeFilter(value)}
                      className={cn(
                        'shrink-0 px-4 py-1.5 rounded-full text-sm font-normal transition-colors duration-200',
                        focusRing,
                        'min-h-11',
                        typeFilter === value ? segmentedSelected : segmentedRest,
                      )}
                    >
                      {label}
                    </button>
                  ))}
                  <span className="shrink-0 w-px h-5 bg-border-divider mx-1" aria-hidden="true" />
                  {ALL_STATUSES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setFilter(s)}
                      className={cn(
                        'shrink-0 px-4 py-1.5 rounded-full text-sm font-normal transition-colors duration-200',
                        focusRing,
                        'min-h-11',
                        filter === s ? segmentedSelected : segmentedRest,
                      )}
                    >
                      {s === 'all' ? 'All' : STATUS_LABELS[s]}
                      {s !== 'all' && (
                        <span className="ml-1.5 opacity-80">{stats[s]}</span>
                      )}
                    </button>
                  ))}
                </div>

                <div className="sm:ml-auto w-full sm:w-48">
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search…"
                    className={cn(
                      'w-full rounded-lg border border-border-divider bg-page-bg px-3 py-2',
                      'text-sm font-light text-body placeholder:text-muted/50',
                      'focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1',
                      'transition-colors duration-200',
                    )}
                  />
                </div>
              </div>
            )}

            {/* Table */}
            <div className="mt-6">
              {entries.length === 0 ? (
                <div
                  data-testid="vocabulary-empty"
                  className="flex flex-col items-center justify-center gap-6 py-16 px-4 rounded-xl border border-border-divider bg-surface-bg/30 text-center"
                >
                  <p className="font-serif text-lg text-body leading-relaxed max-w-sm">
                    {selected ? `No tracked words in ${selected.name} yet` : 'No tracked words yet'}{' '}
                    &mdash; read a text and tap the words you want to keep.
                  </p>
                  <Button variant="secondary" onClick={() => setAdding(true)} className="px-6 py-2.5 min-h-11">
                    Add a word yourself
                  </Button>
                </div>
              ) : sorted.length === 0 ? (
                <div className="flex items-center justify-center h-32 rounded-xl border border-border-divider bg-surface-bg/30">
                  <p className="font-serif text-base text-muted font-light">
                    No words match your filters.
                  </p>
                </div>
              ) : (
                <VocabularyTable
                  entries={sorted}
                  languageId={selected.id}
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                  onEditCustom={setEditing}
                  onEditStrength={setEditingStrength}
                />
              )}
            </div>
          </>
        )}
      </PageContainer>

      {(reviewing || studyDeckId !== null || practiceCount !== null) && selected && (
        <ReviewSession
          languageId={selected.id}
          deckId={studyDeckId ?? undefined}
          randomCount={practiceCount ?? undefined}
          onClose={handleReviewClose}
        />
      )}

      {choosingPractice && (
        <PracticeCountModal
          total={entries.length}
          onStart={(count) => { setChoosingPractice(false); setPracticeCount(count) }}
          onClose={() => setChoosingPractice(false)}
        />
      )}

      {selected && (
        <AddWordModal
          languageId={selected.id}
          open={adding}
          onClose={() => setAdding(false)}
          onSuccess={refresh}
        />
      )}

      {addingPhrase && selected && (
        <AddPhraseModal
          languageId={selected.id}
          onClose={() => setAddingPhrase(false)}
          onCreated={refresh}
        />
      )}

      {resetting && selected && (
        <ResetVocabularyModal
          languageId={selected.id}
          languageName={selected.name}
          entries={entries}
          decks={decks}
          onClose={() => setResetting(false)}
          onReset={refresh}
        />
      )}

      {editingStrength && selected && (
        <EditStrengthModal
          entry={editingStrength}
          languageId={selected.id}
          onClose={() => setEditingStrength(null)}
          onSaved={refresh}
        />
      )}

      {editing && selected && (
        editing.card_type === 'phrase' ? (
          <EditPhraseModal
            entry={editing}
            languageId={selected.id}
            onClose={() => setEditing(null)}
            onSuccess={refresh}
          />
        ) : (
          <EditCustomCardModal
            entry={editing}
            languageId={selected.id}
            onClose={() => setEditing(null)}
            onSuccess={refresh}
          />
        )
      )}
    </AppLayout>
  )
}

/* ─── Header menus ──────────────────────────────────────── */

const menuTrigger = cn(
  'inline-flex items-center rounded-full py-2.5 min-h-11',
  'text-sm font-normal tracking-wide transition-colors duration-200',
  softFill,
  focusRing,
)

function MenuItem({ onSelect, className, children }: { onSelect: () => void; className?: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onSelect}
      className={cn(
        'flex w-full items-center min-h-11 px-3 rounded-md text-left text-sm font-light text-body',
        'hover:bg-surface-bg transition-colors duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
        className,
      )}
    >
      {children}
    </button>
  )
}

/* ─── Skeleton ──────────────────────────────────────────── */

function VocabularySkeleton() {
  return (
    <div data-testid="vocabulary-skeleton" aria-hidden="true">
      <div className="flex items-center justify-between gap-4">
        <Skeleton className="h-9 w-48" />
        <Skeleton className="h-11 w-36 rounded-full" />
      </div>
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-lg" />)}
      </div>
      <div className="mt-10 rounded-xl border border-border-divider divide-y divide-border-divider/50">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-4">
            <Skeleton line className="h-4 w-1/4" />
            <Skeleton line className="h-4 w-1/3" />
            <Skeleton line className="ml-auto h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  )
}

/* ─── Stats card ────────────────────────────────────────── */

/**
 * One count in the stats row, wearing its own status colour so the row reads as the
 * same four steps as the badges in the table below it rather than four grey boxes.
 */
function StatCard({ status, label, count }: { status: WordStatus; label: string; count: number }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border-divider bg-card">
      <span aria-hidden="true" className={cn('block h-1', STATUS_FILLS[status])} />
      <div className="px-2.5 py-2 sm:px-4 sm:py-3">
        <p className="text-[11px] sm:text-xs text-muted font-light truncate">{label}</p>
        <p className="font-serif text-xl sm:text-2xl font-medium leading-tight mt-0.5 text-heading">
          {count}
        </p>
      </div>
    </div>
  )
}

/* ─── Table ─────────────────────────────────────────────── */

function VocabularyTable({
  entries, languageId, sortKey, sortDir, onSort, onEditCustom, onEditStrength,
}: {
  entries: VocabularyEntry[]
  languageId: number
  sortKey: SortKey
  sortDir: SortDir
  onSort: (key: SortKey) => void
  onEditCustom: (entry: CustomVocabularyEntry) => void
  onEditStrength: (entry: WordVocabularyEntry) => void
}) {
  const bodyRef = useStaggerIn<HTMLTableSectionElement>(entries.length > 0)
  const cardsRef = useStaggerIn<HTMLUListElement>(entries.length > 0)
  const isPhone = useIsPhone()
  const key = (entry: VocabularyEntry) =>
    entry.type === 'word' ? `w-${entry.word_id}` : `c-${entry.custom_flashcard_id}`

  // The table needs 540px to stay legible, which is wider than a phone screen — it
  // used to become a sideways scroller. One card per entry instead.
  if (isPhone) {
    return (
      <ul ref={cardsRef} data-testid="vocabulary-cards" className="space-y-2.5">
        {entries.map((entry) => (
          <VocabularyCard
            key={key(entry)}
            entry={entry}
            languageId={languageId}
            onEditCustom={onEditCustom}
            onEditStrength={onEditStrength}
          />
        ))}
      </ul>
    )
  }

  return (
    <div className="rounded-xl border border-border-divider">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border-divider bg-surface-bg/50">
            <SortableHeader
              label="Word"
              sortKey="lemma"
              currentKey={sortKey}
              dir={sortDir}
              onSort={onSort}
              className="pl-4 sm:pl-5 w-[22%]"
            />
            <th className="py-3 px-3 text-left text-xs font-medium uppercase tracking-wider text-muted">
              Translation
            </th>
            <SortableHeader
              label="Status"
              sortKey="status"
              currentKey={sortKey}
              dir={sortDir}
              onSort={onSort}
            />
            <th className="py-3 px-3 text-left text-xs font-medium uppercase tracking-wider text-muted">
              Strength
            </th>
            <SortableHeader
              label="Next review"
              sortKey="next_review_at"
              currentKey={sortKey}
              dir={sortDir}
              onSort={onSort}
            />
            <th className="py-3 px-3 pr-4 sm:pr-5 w-px whitespace-nowrap" aria-label="Actions" />
          </tr>
        </thead>
        <tbody ref={bodyRef} className="divide-y divide-border-divider/50">
          {entries.map((entry) => (
            <VocabularyRow
              key={key(entry)}
              entry={entry}
              languageId={languageId}
              onEditCustom={onEditCustom}
              onEditStrength={onEditStrength}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

/* ─── Sortable header ───────────────────────────────────── */

function SortableHeader({
  label, sortKey, currentKey, dir, onSort, className,
}: {
  label: string
  sortKey: SortKey
  currentKey: SortKey
  dir: SortDir
  onSort: (key: SortKey) => void
  className?: string
}) {
  const active = sortKey === currentKey
  return (
    <th className={cn('py-3 px-3 text-left', className)}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          'flex items-center gap-1 text-xs font-medium uppercase tracking-wider',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 rounded',
          active ? 'text-heading' : 'text-muted hover:text-body',
          'transition-colors duration-200',
        )}
      >
        {label}
        {active && (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="shrink-0" aria-hidden="true">
            <path
              d={dir === 'asc' ? 'M2 6l3-3 3 3' : 'M2 4l3 3 3-3'}
              stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
    </th>
  )
}
